import { useEffect, useReducer } from 'react'
import axios from '@fs/zion-axios'
import { i18n } from '@fs/zion-locale'
import useDynamicNavigationFlagContext from './useDynamicNavigationFlagContext'

// Store for dynamic query parameters
let dynamicQueryParams = ''
const listeners = new Set()

export function setDynamicQueryParams(params) {
  dynamicQueryParams = params
  listeners.forEach((listener) => listener())
}

// If language changes we need to refresh the navigation
i18n.on('languageChanged', () => {
  listeners.forEach((listener) => listener())
})

export function getDynamicLinks(flags) {
  const baseUrl = `/service/home/utils/chef/header?lang=${i18n.language}`
  const queryParams = `${dynamicQueryParams}${dynamicQueryParams && flags ? '&' : ''}${flags ? `forceFeatureFlags=${flags}` : ''}`
  const url = queryParams ? `${baseUrl}&${queryParams}` : baseUrl
  return axios.get(url).then((response) => response.data)
}

function flagOverrideToString(flagOverrides) {
  if (!flagOverrides) return ''
  return Object.entries(flagOverrides)
    .map(([key, value]) => `${value.treatment === 'on' ? '' : '!'}${key}`)
    .join(',')
}

function reducer(state, action) {
  const { type, headerNavigation, accountNavigation, helpNavigation, flags } = action
  switch (type) {
    case 'SET_NAVIGATION':
      return {
        ...state,
        isLoading: false,
        isError: false,
        headerNavigation,
        accountNavigation,
        helpNavigation,
      }
    case 'SET_NAVIGATION_ERROR':
      return {
        ...state,
        isLoading: false,
        isError: true,
        initiateFetch: false,
      }
    case 'FETCHING_NAVIGATION':
      return {
        ...state,
        isLoading: true,
        isError: false,
        initiateFetch: false,
      }
    case 'REFETCH_NAVIGATION':
      return {
        ...state,
        isLoading: true,
        isError: false,
        initiateFetch: true,
        flags: flags ?? state.flags,
      }
    default:
      return state
  }
}

// used to prevent race conditions when fetching the header more than once
let fetchCount = 0
export default function useDynamicNavigation() {
  const flagOverrides = useDynamicNavigationFlagContext()
  const [state, dispatch] = useReducer(reducer, { isLoading: true, isError: false, initiateFetch: true, flags: null })

  // Subscribe to any changes
  useEffect(() => {
    const listener = () => {
      dispatch({ type: 'REFETCH_NAVIGATION' })
    }
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  useEffect(() => {
    if (state.flags === null || flagOverrideToString(flagOverrides) !== state.flags) {
      dispatch({ type: 'REFETCH_NAVIGATION', flags: flagOverrideToString(flagOverrides) })
    } else if (state.initiateFetch) {
      const currentFetchCount = ++fetchCount
      dispatch({ type: 'FETCHING_NAVIGATION' })
      getDynamicLinks(state.flags)
        .then(({ header, account: accountArray, help }) => {
          if (currentFetchCount === fetchCount) {
            let account = null
            if (accountArray?.length) {
              account = accountArray[0]
            }
            dispatch({
              type: 'SET_NAVIGATION',
              headerNavigation: header,
              accountNavigation: account,
              helpNavigation: help,
            })
          }
        })
        .catch((error) => {
          if (currentFetchCount === fetchCount) {
            console.error(error)
            dispatch({ type: 'SET_NAVIGATION_ERROR' })
          }
        })
    }
    // language only to trigger a refresh when the language changes
  }, [state.initiateFetch, state.flags, flagOverrides])
  return state
}
